import {
  ChakraBox,
  ChakraFlex,
  ChakraGrid,
  ChakraGridItem,
  MaudCardFrame,
} from '@equip.health/ui';
import { useMemo } from 'react';
import PatientNoteTreatmentAttribute from './PatientNoteTreatmentAttribute';
import { useGetPatientTreatmentEpisodesQuery } from '~/store/services/clinicalData';
import { useGetTreatmentEpisodeAttributesQuery } from '~/store/services/treatmentAttributes';
import Loading from '~/pages/loading';
import {
  BaseTreatmentAttribute,
  TreatmentAttributeEnum,
} from '~/lib/types/treatmentAttributes/treatmentAttributes';
import { last } from 'lodash';
import { getTreatmentAttributeEnum } from '~/lib/util/treatmentEpisodes.util';
import { useGetTreatmentModalitiesEnumQuery } from '~/store/services/enumerations';
import { PatientReadonlyNoteCollapse } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientReadonlyNoteCollapse';

type PatientNoteTreatmentOverviewSectionProps = {
  id: string; // TODO remove ID?
  patientId: string;
  noteId: string;
};

const PatientNoteTreatmentOverviewSection = ({
  patientId,
  noteId,
}: PatientNoteTreatmentOverviewSectionProps) => {
  const {
    data: patientTreatmentEpisodes,
    isLoading: isGetPatientTreatmentEpisodesLoading,
  } = useGetPatientTreatmentEpisodesQuery(patientId);
  const activeTreatmentEpisode = patientTreatmentEpisodes?.data.find(
    (episode) => episode.inTreatment,
  );
  const {
    data: treatmentEpisodeAttributes,
    isLoading: isGetTreatmentEpisodeAttributesLoading,
  } = useGetTreatmentEpisodeAttributesQuery(
    {
      noteId,
      treatmentEpisodeId: activeTreatmentEpisode?.treatmentEpisodeId,
    },
    { skip: !activeTreatmentEpisode },
  );

  const {
    data: treatmentModalities,
    isLoading: isGetTreatmentModalitiesLoading,
  } = useGetTreatmentModalitiesEnumQuery();

  const isLoading =
    isGetPatientTreatmentEpisodesLoading ||
    isGetTreatmentEpisodeAttributesLoading ||
    isGetTreatmentModalitiesLoading;

  const hasAttributes = Object.keys(treatmentEpisodeAttributes ?? {}).some(
    (attributeKey) => treatmentEpisodeAttributes[attributeKey].length > 0,
  );

  const getAttributeAtNoteCreation = (attributes: BaseTreatmentAttribute[]) => {
    const firstAttribute = attributes[0];
    return !firstAttribute?.noteId || firstAttribute.noteId !== noteId
      ? firstAttribute
      : undefined;
  };

  const getLatestAttributeWithNoteId = (
    attributes: BaseTreatmentAttribute[],
  ) => {
    const lastAttribute = last(attributes);
    return lastAttribute?.noteId === noteId ? lastAttribute : undefined;
  };

  const latestModalityAttributeWithNoteId = getLatestAttributeWithNoteId(
    treatmentEpisodeAttributes?.modalities,
  );
  const isFormAttributeVisible = useMemo(() => {
    if (!treatmentEpisodeAttributes?.forms.length) {
      return false;
    }

    if (!latestModalityAttributeWithNoteId) {
      return true;
    }

    const latestModalityAttributeWithNoteIdEnum = getTreatmentAttributeEnum(
      latestModalityAttributeWithNoteId,
    ) as TreatmentAttributeEnum;

    return (
      treatmentModalities?.find(
        (modality) =>
          modality.code === latestModalityAttributeWithNoteIdEnum.code,
      )?.treatmentForms.length > 0
    );
  }, [
    latestModalityAttributeWithNoteId,
    treatmentEpisodeAttributes?.forms.length,
    treatmentModalities,
  ]);

  if (isLoading) {
    return <Loading />;
  }

  return hasAttributes ? (
    <ChakraBox marginTop="12px">
      <PatientReadonlyNoteCollapse title="Overview">
        <MaudCardFrame id="patient_note_treatment_overview_card">
          <ChakraGrid columnGap="36px" templateColumns="repeat(2, 1fr)">
            <ChakraGridItem>
              <ChakraFlex flexDirection="column" gap="8px">
                {treatmentEpisodeAttributes.modalities.length > 0 && (
                  <PatientNoteTreatmentAttribute
                    attributeAtNoteCreation={getAttributeAtNoteCreation(
                      treatmentEpisodeAttributes.modalities,
                    )}
                    latestAttributeWithNoteId={
                      latestModalityAttributeWithNoteId
                    }
                    title="Modality"
                  />
                )}
                {isFormAttributeVisible && (
                  <PatientNoteTreatmentAttribute
                    attributeAtNoteCreation={getAttributeAtNoteCreation(
                      treatmentEpisodeAttributes.forms,
                    )}
                    latestAttributeWithNoteId={getLatestAttributeWithNoteId(
                      treatmentEpisodeAttributes.forms,
                    )}
                    title="Form"
                  />
                )}
                {treatmentEpisodeAttributes.phaseStages.length > 0 && (
                  <PatientNoteTreatmentAttribute
                    attributeAtNoteCreation={getAttributeAtNoteCreation(
                      treatmentEpisodeAttributes.phaseStages,
                    )}
                    latestAttributeWithNoteId={getLatestAttributeWithNoteId(
                      treatmentEpisodeAttributes.phaseStages,
                    )}
                    title="Phase/stage"
                  />
                )}
                {treatmentEpisodeAttributes.levels.length > 0 && (
                  <PatientNoteTreatmentAttribute
                    attributeAtNoteCreation={getAttributeAtNoteCreation(
                      treatmentEpisodeAttributes.levels,
                    )}
                    latestAttributeWithNoteId={getLatestAttributeWithNoteId(
                      treatmentEpisodeAttributes.levels,
                    )}
                    title="Level"
                  />
                )}
              </ChakraFlex>
            </ChakraGridItem>
            <ChakraGridItem>
              <ChakraFlex flexDirection="column" gap="8px">
                {treatmentEpisodeAttributes.discussionsOfWeight.length > 0 && (
                  <PatientNoteTreatmentAttribute
                    attributeAtNoteCreation={getAttributeAtNoteCreation(
                      treatmentEpisodeAttributes.discussionsOfWeight,
                    )}
                    latestAttributeWithNoteId={getLatestAttributeWithNoteId(
                      treatmentEpisodeAttributes.discussionsOfWeight,
                    )}
                    title="Weight discussion"
                  />
                )}
                {treatmentEpisodeAttributes.saferStages.length > 0 && (
                  <PatientNoteTreatmentAttribute
                    attributeAtNoteCreation={getAttributeAtNoteCreation(
                      treatmentEpisodeAttributes.saferStages,
                    )}
                    latestAttributeWithNoteId={getLatestAttributeWithNoteId(
                      treatmentEpisodeAttributes.saferStages,
                    )}
                    title="SAFER stage"
                  />
                )}
                {treatmentEpisodeAttributes.refeedingRisks.length > 0 && (
                  <PatientNoteTreatmentAttribute
                    attributeAtNoteCreation={getAttributeAtNoteCreation(
                      treatmentEpisodeAttributes.refeedingRisks,
                    )}
                    latestAttributeWithNoteId={getLatestAttributeWithNoteId(
                      treatmentEpisodeAttributes.refeedingRisks,
                    )}
                    title="Refeeding risk"
                  />
                )}
                {treatmentEpisodeAttributes.medicalStabilities.length > 0 && (
                  <PatientNoteTreatmentAttribute
                    attributeAtNoteCreation={getAttributeAtNoteCreation(
                      treatmentEpisodeAttributes.medicalStabilities,
                    )}
                    latestAttributeWithNoteId={getLatestAttributeWithNoteId(
                      treatmentEpisodeAttributes.medicalStabilities,
                    )}
                    title="Medical stability"
                  />
                )}
                {treatmentEpisodeAttributes.psychiatricStabilities.length >
                  0 && (
                  <PatientNoteTreatmentAttribute
                    attributeAtNoteCreation={getAttributeAtNoteCreation(
                      treatmentEpisodeAttributes.psychiatricStabilities,
                    )}
                    latestAttributeWithNoteId={getLatestAttributeWithNoteId(
                      treatmentEpisodeAttributes.psychiatricStabilities,
                    )}
                    title="Psychiatric stability"
                  />
                )}
              </ChakraFlex>
            </ChakraGridItem>
          </ChakraGrid>
        </MaudCardFrame>
      </PatientReadonlyNoteCollapse>
    </ChakraBox>
  ) : null;
};

export default PatientNoteTreatmentOverviewSection;
